<template>
  <Teleport to="body">
    <Transition name="hotkey-help-fade">
      <div v-if="modelValue" class="hotkey-help-overlay">
        <div class="hotkey-help-overlay__backdrop" @click="close" />
        <div class="hotkey-help-overlay__panel">
          <div class="hotkey-help-overlay__header">
            <h3>{{ t('hotkeys.title') }}</h3>
            <el-button text :icon="Close" @click="close" />
          </div>
          <div class="hotkey-help-overlay__content">
            <div v-for="context in activeBindings" :key="context.contextName" class="hotkey-help-overlay__context">
              <div v-for="binding in context.bindings" :key="binding.key" class="hotkey-help-overlay__binding">
                <kbd class="hotkey-help-overlay__kbd">{{ formatLabel(binding) }}</kbd>
                <span class="hotkey-help-overlay__description">{{ t(binding.description!) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Close } from '@element-plus/icons-vue'
import { onKeyDown, useScrollLock } from '@vueuse/core'

import { formatHotkeyLabel } from '@/composables/hotkeys/formatHotkeyLabel'
import type { HotkeyBinding } from '@/composables/hotkeys/hotkeys.types'
import { HotkeyService } from '@/composables/hotkeys/hotkeyService'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const { t } = useI18n()

const activeBindings = computed(() => {
  if (!props.modelValue) return []
  const service = HotkeyService.getInstance()
  return service.getActiveBindings()
})

function formatLabel(binding: HotkeyBinding): string {
  return formatHotkeyLabel(binding)
}

function close(): void {
  emit('update:modelValue', false)
}

const isLocked = useScrollLock(document.body)

watch(
  () => props.modelValue,
  visible => {
    isLocked.value = visible
  }
)

onKeyDown('Escape', event => {
  if (!props.modelValue) return
  event.stopPropagation()
  event.preventDefault()
  close()
})
</script>

<style lang="scss" scoped>
@use '@/styles/variables';

.hotkey-help-overlay {
  position: fixed;
  inset: 0;
  z-index: variables.$zIndexOverlay;
  display: flex;
  align-items: center;
  justify-content: center;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  &__panel {
    position: relative;
    background: var(--el-bg-color);
    border-radius: 8px;
    padding: 24px;
    min-width: 320px;
    max-width: 480px;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: var(--el-box-shadow);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 18px;
    }
  }

  &__context + &__context {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--el-border-color-lighter);
  }

  &__binding {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
  }

  &__kbd {
    display: inline-block;
    padding: 3px 8px;
    font-size: 12px;
    font-family: monospace;
    line-height: 1.4;
    color: var(--el-text-color-regular);
    background: var(--el-fill-color-light);
    border: 1px solid var(--el-border-color);
    border-radius: 4px;
    min-width: 24px;
    text-align: center;
  }

  &__description {
    color: var(--el-text-color-regular);
    font-size: 14px;
    margin-left: 16px;
  }
}

.hotkey-help-fade-enter-active,
.hotkey-help-fade-leave-active {
  transition: opacity 0.15s ease;
}

.hotkey-help-fade-enter-from,
.hotkey-help-fade-leave-to {
  opacity: 0;
}
</style>
